/**
 * Configuration tab — mirrors the v4 "Tokeniser Assumptions" tab sections
 * plus per-model pricing, PT sizing, runtime assumptions, and add-ons.
 * All values feed into estimates via per-request overrides (held in browser
 * session; not persisted to the server).
 */

import type { Currency, EstimationParams, ModelInfo, ModelPricing } from '@tcc/shared';
import { useState } from 'react';
import { PT_ROWS, RUNTIME_ROWS, SIZING_ROWS } from '../../constants';
import { CharTokenRow } from '../CharTokenRow/CharTokenRow';
import { DocumentRow } from '../DocumentRow/DocumentRow';
import { NumCell } from '../NumCell/NumCell';
import { Row } from '../Row/Row';
import { Section } from '../Section/Section';
import { TableHeader } from '../TableHeader/TableHeader';

const TIERS = ['standard', 'priority', 'flexBatch'] as const;
const TIER_LABELS: Record<(typeof TIERS)[number], string> = {
	standard: 'Standard',
	priority: 'Priority',
	flexBatch: 'Flex/Batch',
};
const BANDS = ['le200k', 'gt200k'] as const;
const BAND_LABELS: Record<(typeof BANDS)[number], string> = { le200k: '≤200K', gt200k: '>200K' };
const FIELDS = ['input', 'output', 'cached'] as const;
const FIELD_LABELS: Record<(typeof FIELDS)[number], string> = { input: 'In', output: 'Out', cached: 'Cache' };
const CCY_SYMBOL: Record<Currency, string> = { GBP: '£', USD: '$' };

export interface ConfigProps {
	models: ModelInfo[];
	params: EstimationParams;
	onModelsChange: (models: ModelInfo[]) => void;
	onParamsChange: (params: EstimationParams) => void;
	onReset: () => void;
}

export function Config({ models, params, onModelsChange, onParamsChange, onReset }: ConfigProps) {
	const [selectedId, setSelectedId] = useState(models[0]?.modelId ?? '');
	// GBP-first tool: the model-rates table opens in GBP. With no native GBP
	// pricing seeded, it shows a read-only £ table derived from USD base × FX.
	const [pricingCurrency, setPricingCurrency] = useState<Currency>('GBP');
	const model = models.find((m) => m.modelId === selectedId) ?? models[0];

	const updateModel = (patch: Partial<ModelInfo>) =>
		onModelsChange(models.map((m) => (m.modelId === model?.modelId ? { ...m, ...patch } : m)));
	const updateWeights = (patch: Partial<ModelInfo['imageTokenWeights']>) =>
		updateModel({ imageTokenWeights: { ...model.imageTokenWeights, ...patch } });

	/** Build a full ModelPricing scaffold from the model's flat USD rates (all tiers/bands equal). */
	const pricingScaffold = (): ModelPricing => {
		const flat = { input: model.inputPricePerMillion, output: model.outputPricePerMillion, cached: 0 };
		return {
			standard: { le200k: { ...flat }, gt200k: { ...flat } },
			priority: { le200k: { ...flat }, gt200k: { ...flat } },
			flexBatch: { le200k: { ...flat }, gt200k: { ...flat } },
		};
	};

	/** Deep-copy a ModelPricing so seeded native pricing doesn't share refs with USD. */
	const clonePricing = (p: ModelPricing): ModelPricing => ({
		standard: { le200k: { ...p.standard.le200k }, gt200k: { ...p.standard.gt200k } },
		priority: { le200k: { ...p.priority.le200k }, gt200k: { ...p.priority.gt200k } },
		flexBatch: { le200k: { ...p.flexBatch.le200k }, gt200k: { ...p.flexBatch.gt200k } },
	});

	/** Build a display-only pricing table by converting USD base rates × FX (rounded to 3dp). */
	const convertPricing = (p: ModelPricing, fx: number): ModelPricing => {
		const conv = (r: ModelPricing['standard']['le200k']) => ({
			input: Math.round(r.input * fx * 1000) / 1000,
			output: Math.round(r.output * fx * 1000) / 1000,
			cached: Math.round(r.cached * fx * 1000) / 1000,
		});
		return {
			standard: { le200k: conv(p.standard.le200k), gt200k: conv(p.standard.gt200k) },
			priority: { le200k: conv(p.priority.le200k), gt200k: conv(p.priority.gt200k) },
			flexBatch: { le200k: conv(p.flexBatch.le200k), gt200k: conv(p.flexBatch.gt200k) },
		};
	};

	const nativePricing = model?.pricingByCurrency ?? {};
	/** Native vendor pricing exists for the currently-selected (non-USD) pricing currency. */
	const hasNativePricing = pricingCurrency !== 'USD' && nativePricing[pricingCurrency] !== undefined;
	/** The pricing object currently being edited: USD base, or the selected native currency (if present). */
	const editingPricing: ModelPricing | undefined = !model
		? undefined
		: pricingCurrency === 'USD'
			? (model.pricing ?? pricingScaffold())
			: nativePricing[pricingCurrency];

	/**
	 * Update one rate cell for the currently-selected pricing currency. USD edits
	 * the base `pricing`; GBP edits the native `pricingByCurrency[ccy]`.
	 */
	const updateRate = (
		tier: 'standard' | 'priority' | 'flexBatch',
		band: 'le200k' | 'gt200k',
		field: 'input' | 'output' | 'cached',
		v: number,
	) => {
		if (pricingCurrency === 'USD') {
			const base = model.pricing ?? pricingScaffold();
			updateModel({ pricing: { ...base, [tier]: { ...base[tier], [band]: { ...base[tier][band], [field]: v } } } });
		} else {
			const base = nativePricing[pricingCurrency] ?? pricingScaffold();
			updateModel({
				pricingByCurrency: {
					...nativePricing,
					[pricingCurrency]: { ...base, [tier]: { ...base[tier], [band]: { ...base[tier][band], [field]: v } } },
				},
			});
		}
	};

	/** Add a native vendor-pricing table for the selected currency, seeded from the USD figures as a starting point. */
	const addNativePricing = () => {
		if (pricingCurrency === 'USD') return;
		const seed = model.pricing ?? pricingScaffold();
		updateModel({ pricingByCurrency: { ...nativePricing, [pricingCurrency]: clonePricing(seed) } });
	};

	/** Remove native pricing for the selected currency (reverts to USD × FX). */
	const removeNativePricing = () => {
		if (pricingCurrency === 'USD') return;
		const next = { ...nativePricing };
		delete next[pricingCurrency];
		updateModel({ pricingByCurrency: next });
	};

	const setP = (patch: Partial<EstimationParams>) => onParamsChange({ ...params, ...patch });
	const setTxt = (patch: Partial<typeof params.textInputTokens>) =>
		setP({ textInputTokens: { ...params.textInputTokens, ...patch } });
	const setOut = (patch: Partial<typeof params.outputSizeBaseTokens>) =>
		setP({ outputSizeBaseTokens: { ...params.outputSizeBaseTokens, ...patch } });
	const setPerms = (patch: Partial<typeof params.sensitivityPermutations>) =>
		setP({ sensitivityPermutations: { ...params.sensitivityPermutations, ...patch } });
	const setPtSizing = (key: keyof typeof params.ptSizing, v: number) =>
		setP({ ptSizing: { ...params.ptSizing, [key]: { perSecondThroughputPerGsu: v } } });
	const setRuntime = (key: keyof typeof params.runtimeAssumptions, field: 'vcpuPerRps' | 'gibRamPerRps', v: number) =>
		setP({
			runtimeAssumptions: {
				...params.runtimeAssumptions,
				[key]: { ...params.runtimeAssumptions[key], [field]: v },
			},
		});
	const setSizingRange = (key: keyof typeof params.sizingRanges, field: 'min' | 'max', v: number) =>
		setP({
			sizingRanges: {
				...params.sizingRanges,
				[key]: { ...params.sizingRanges[key], [field]: Number.isFinite(v) ? Math.max(1, v) : 1 },
			},
		});

	const sym = CCY_SYMBOL[pricingCurrency];
	const isDerived = !editingPricing;
	const displayPricing = model
		? (editingPricing ?? convertPricing(model.pricing ?? pricingScaffold(), params.fxRateUsdToGbp))
		: undefined;

	return (
		<div className="sg">
			<div className="sc">
				<div className="tbar">
					<span className="wm">Lloyds Banking Group</span>
				</div>

				{/* §1 Content size ranges */}
				<Section
					badge="1"
					title="Content size ranges"
					sub="Min/max bounds for the step 3 'Estimate the typical size of each input' sliders, per content type."
				/>
				<div className="cfg-table">
					<div className="cfg-table-hdr">
						<span className="cfg-table-hdr-lbl">Content type</span>
						<span className="cfg-col-hdr">Min</span>
						<span className="cfg-col-hdr">Max</span>
					</div>
					{SIZING_ROWS.map(({ key, label }) => (
						<div key={key} className="cfg-table-row">
							<span className="cfg-row-lbl">{label}</span>
							<input
								type="number"
								min={1}
								step={1}
								value={params.sizingRanges[key].min}
								onChange={(e) => setSizingRange(key, 'min', Number(e.target.value))}
								aria-label={`${label} min`}
								className="cfg-input-w90"
							/>
							<input
								type="number"
								min={1}
								step={1}
								value={params.sizingRanges[key].max}
								onChange={(e) => setSizingRange(key, 'max', Number(e.target.value))}
								aria-label={`${label} max`}
								className="cfg-input-w90"
							/>
						</div>
					))}
				</div>

				{/* §2 Text sizing — input */}
				<Section
					badge="2"
					title="Text sizing — input"
					sub="Characters and tokens per text unit (v4 Tokeniser §1). Used for primary text input and additional context."
				/>
				<TableHeader cols={['Unit', 'Characters (≈ tokens)']} />
				<CharTokenRow
					label="One sentence"
					tokens={params.textInputTokens.sentence}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setTxt({ sentence: v })}
				/>
				<CharTokenRow
					label="A paragraph"
					tokens={params.textInputTokens.paragraph}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setTxt({ paragraph: v })}
				/>
				<CharTokenRow
					label="A page"
					tokens={params.textInputTokens.page}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setTxt({ page: v })}
				/>
				<DocumentRow
					label="A document"
					tokens={params.textInputTokens.document}
					pageTokens={params.textInputTokens.page}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setTxt({ document: v })}
				/>
				<Row label="Characters per token" value={params.bytesPerRagToken} onChange={() => {}} disabled />

				{/* §3 Modality conversion */}
				<Section
					badge="3"
					title="Modality conversion"
					sub="Voice tokens per minute · Video with audio tokens per minute (v4 Tokeniser §2)."
				/>
				<Row
					label="Voice tokens per minute"
					value={params.tokensPerVoiceMinute}
					onChange={(v) => setP({ tokensPerVoiceMinute: v })}
				/>
				<Row
					label="Video with audio tokens per minute"
					value={params.tokensPerVideoMinute}
					onChange={(v) => setP({ tokensPerVideoMinute: v })}
				/>

				{/* §4 Image sizing */}
				<Section
					badge="4"
					title="Image sizing"
					sub="Approx tokens per image by size band (v4 Tokeniser §3). Select a model to edit its image weights."
				/>
				<label className="flbl">
					Model
					<select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="cfg-model-select">
						{models.map((m) => (
							<option key={m.modelId} value={m.modelId}>
								{m.displayName}
							</option>
						))}
					</select>
				</label>
				{model && (
					<div className="cfg-model-table">
						<TableHeader cols={['Size band', 'Max px', 'Tokens']} />
						{(
							[
								['small', 'Icon / thumbnail (≤384px)', 384],
								['medium', 'Small image (≤512px)', 512],
								['large', 'Standard image (≤1024px)', 1024],
								['hd', 'HD / screenshot (≤1920px)', 1920],
								['ultra', '4K / very large (≤99999px)', 99999],
							] as const
						).map(([key, label]) => (
							<Row
								key={key}
								label={label}
								value={model.imageTokenWeights[key]}
								onChange={(v) => updateWeights({ [key]: v })}
							/>
						))}
					</div>
				)}

				{/* §5 Output sizing */}
				<Section
					badge="5"
					title="Output sizing"
					sub="Base response size per output unit, entered as characters with the token equivalent shown (v4 Tokeniser §4)."
				/>
				<TableHeader cols={['Output unit', 'Characters (≈ tokens)']} />
				<CharTokenRow
					label="One sentence"
					tokens={params.outputSizeBaseTokens.sentence}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setOut({ sentence: v })}
				/>
				<CharTokenRow
					label="A paragraph"
					tokens={params.outputSizeBaseTokens.paragraph}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setOut({ paragraph: v })}
				/>
				<CharTokenRow
					label="A page"
					tokens={params.outputSizeBaseTokens.page}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setOut({ page: v })}
				/>
				<CharTokenRow
					label="A document"
					tokens={params.outputSizeBaseTokens.document}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setOut({ document: v })}
				/>

				{/* §6 Decision overhead */}
				<Section
					badge="6"
					title="Decision overhead"
					sub="Reasoning paragraph size (entered as characters, token equivalent shown) and decision-complexity permutations. Decision counts are entered per 100 runs."
				/>
				<TableHeader cols={['Paragraph type', 'Characters (≈ tokens)']} />
				<CharTokenRow
					label="Chain of Thought (CoT)"
					tokens={params.chainOfThoughtPerDecision}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setP({ chainOfThoughtPerDecision: v })}
				/>
				<CharTokenRow
					label="Rationale for decision"
					tokens={params.rationalePerDecision}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setP({ rationalePerDecision: v })}
				/>
				<CharTokenRow
					label="Decision outcome"
					tokens={params.perDecisionTokenCost}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setP({ perDecisionTokenCost: v })}
				/>
				<div className="cfg-decision-total">
					Total per decision:{' '}
					{params.chainOfThoughtPerDecision + params.rationalePerDecision + params.perDecisionTokenCost} tokens
				</div>
				<TableHeader cols={['Complexity', 'Permutations']} />
				<Row
					label="Simple — low-impact / routine"
					value={params.sensitivityPermutations.simple}
					step={1}
					onChange={(v) => setPerms({ simple: v })}
				/>
				<Row
					label="Complex — sensitive / high-impact"
					value={params.sensitivityPermutations.complex}
					step={1}
					onChange={(v) => setPerms({ complex: v })}
				/>

				{/* §V Volumetrics basis */}
				<Section
					badge="V"
					title="Volumetrics basis"
					sub="Days used on the Input sizing step to convert the daily volume into monthly and yearly totals. Monthly = per day × days-in-month; yearly = per day × days-in-year. Defaults: 365/12 (≈30.42) days per month, 365 days per year."
				/>
				<Row
					label="Days in month"
					step={0.01}
					value={params.daysPerMonth}
					onChange={(v) => setP({ daysPerMonth: v })}
				/>
				<Row label="Days in year" step={1} value={params.daysPerYear} onChange={(v) => setP({ daysPerYear: v })} />

				{/* §£ Model rates & FX */}
				<Section
					badge="£"
					title="Model rates &amp; FX"
					sub="Per-model tiered input/output/cached prices across Standard, Priority, and Flex/Batch tiers. Edit USD base rates, or add native vendor-published prices in GBP (used directly, no FX). The USD→GBP rate below applies only when a currency has no native pricing."
				/>
				<Row
					label="FX rate USD→GBP"
					step={0.01}
					value={params.fxRateUsdToGbp}
					onChange={(v) => setP({ fxRateUsdToGbp: v })}
				/>
				<Row
					label="System prompt baseline (tokens)"
					value={params.systemPromptBaselineTokens}
					onChange={(v) => setP({ systemPromptBaselineTokens: v })}
				/>
				{model && displayPricing && (
					<div className="cfg-pricing">
						{/* biome-ignore lint/a11y/useSemanticElements: toggle buttons grouped by role="group" — <fieldset> is inappropriate here as these are not form inputs */}
						<div role="group" aria-label="Pricing currency" className="period-group">
							{(['USD', 'GBP'] as Currency[]).map((c) => (
								<button
									key={c}
									type="button"
									onClick={() => setPricingCurrency(c)}
									aria-pressed={pricingCurrency === c}
									className="period-btn period-btn--dark"
								>
									{CCY_SYMBOL[c]} {c}
									{c === 'USD' ? ' (base)' : ''}
								</button>
							))}
						</div>
						<div className="cfg-pricing-title">
							{model.displayName} — {pricingCurrency} / 1M tokens
						</div>
						<div className="cfg-pricing-note">
							{pricingCurrency === 'USD'
								? 'USD base rates. Used directly for USD, and converted via FX for currencies without native pricing.'
								: hasNativePricing
									? `Native vendor-published ${pricingCurrency} prices — applied directly with no FX conversion.`
									: `No native ${pricingCurrency} pricing. Estimates in ${pricingCurrency} use USD × FX. Add native pricing to enter the vendor's published ${pricingCurrency} figures.`}
						</div>
						<table className="cfg-rates">
							<thead>
								<tr>
									<th>Tier</th>
									{BANDS.map((b) =>
										FIELDS.map((f) => (
											<th key={`${b}-${f}`}>
												{BAND_LABELS[b]} {FIELD_LABELS[f]}
											</th>
										)),
									)}
								</tr>
							</thead>
							<tbody>
								{TIERS.map((tier) => (
									<tr key={tier}>
										<td>{TIER_LABELS[tier]}</td>
										{BANDS.map((band) =>
											FIELDS.map((field) => (
												<td key={`${band}-${field}`}>
													{isDerived ? (
														<span className="cfg-derived">{displayPricing[tier][band][field]}</span>
													) : (
														<NumCell
															value={displayPricing[tier][band][field]}
															onChange={(v) => updateRate(tier, band, field, v)}
														/>
													)}
												</td>
											)),
										)}
									</tr>
								))}
							</tbody>
						</table>
						<div className="cfg-pricing-footer">
							Amounts in {sym}
							{pricingCurrency} per 1M tokens.
							{pricingCurrency === 'USD'
								? ` Flat legacy rates: In $${model.inputPricePerMillion} · Out $${model.outputPricePerMillion}`
								: isDerived
									? ` Converted from USD base at FX ${params.fxRateUsdToGbp} — read-only. Add native ${pricingCurrency} pricing to edit exact figures.`
									: ''}
						</div>
						{pricingCurrency !== 'USD' &&
							(isDerived ? (
								<button type="button" className="btnp btn-mt" onClick={addNativePricing}>
									+ Add native {pricingCurrency} vendor pricing
								</button>
							) : (
								<button type="button" className="btns btn-mt" onClick={removeNativePricing}>
									Remove native {pricingCurrency} pricing (use FX)
								</button>
							))}
					</div>
				)}

				{/* §PT Provisioned Throughput sizing */}
				<Section
					badge="PT"
					title="PT Sizing — Provisioned Throughput"
					sub="Requests per second of throughput per GSU, per Gemini model (v4 PT Sizing tab)."
				/>
				<Row
					label="PT cost per GSU per month (USD)"
					step={10}
					value={params.ptCostPerGsuPerMonth}
					onChange={(v) => setP({ ptCostPerGsuPerMonth: v })}
				/>
				<div className="cfg-subsection">
					<TableHeader cols={['Model', 'Req/sec per GSU']} />
					{PT_ROWS.map(({ key, label }) => (
						<Row
							key={key}
							label={label}
							value={params.ptSizing[key].perSecondThroughputPerGsu}
							onChange={(v) => setPtSizing(key, v)}
						/>
					))}
				</div>

				{/* §R Runtime assumptions */}
				<Section
					badge="R"
					title="Runtime Assumptions"
					sub="vCPU and GiB RAM required per request/sec, per agent deployment style (v4 Runtime assumptions tab). Free tiers: 50 vCPU-hr/mo, 100 GiB-hr/mo."
				/>
				<div className="cfg-subsection--scroll">
					<TableHeader cols={['Agent type', 'vCPU / RPS', 'GiB RAM / RPS']} />
					{RUNTIME_ROWS.map(({ key, label }) => (
						<div key={key} className="cfg-table-row">
							<span className="cfg-row-lbl-wide">{label}</span>
							<input
								type="number"
								step={0.01}
								value={params.runtimeAssumptions[key].vcpuPerRps}
								onChange={(e) => setRuntime(key, 'vcpuPerRps', Number(e.target.value))}
								aria-label={`${label} vCPU per RPS`}
								className="cfg-input-flex"
							/>
							<input
								type="number"
								step={0.01}
								value={params.runtimeAssumptions[key].gibRamPerRps}
								onChange={(e) => setRuntime(key, 'gibRamPerRps', Number(e.target.value))}
								aria-label={`${label} GiB RAM per RPS`}
								className="cfg-input-flex"
							/>
						</div>
					))}
				</div>
				<Row
					label="vCPU rate $/vCPU-hr"
					step={0.001}
					value={params.runtimeVcpuRateUsdPerHour}
					onChange={(v) => setP({ runtimeVcpuRateUsdPerHour: v })}
				/>
				<Row
					label="RAM rate $/GiB-hr"
					step={0.001}
					value={params.runtimeRamRateUsdPerGibHour}
					onChange={(v) => setP({ runtimeRamRateUsdPerGibHour: v })}
				/>
				<div className="cfg-runtime-lbl">Agent runtime rates (Pricing tab B):</div>
				<Row
					label="Sessions $ per 1k events stored"
					step={0.01}
					value={params.agentSessionsRateUsd}
					onChange={(v) => setP({ agentSessionsRateUsd: v })}
				/>
				<Row
					label="Memory stored $ per 1k/mo"
					step={0.01}
					value={params.agentMemoryStoredRateUsd}
					onChange={(v) => setP({ agentMemoryStoredRateUsd: v })}
				/>
				<Row
					label="Memory retrieved $ per 1k"
					step={0.01}
					value={params.agentMemoryRetrievedRateUsd}
					onChange={(v) => setP({ agentMemoryRetrievedRateUsd: v })}
				/>
				<div className="cfg-free-tier">Free tiers: 50 vCPU-hr/mo · 100 GiB-hr/mo · 1,000 memory retrieved/mo</div>

				{/* §A Add-ons & Pricing */}
				<Section
					badge="A"
					title="Add-ons &amp; Pricing"
					sub="Model Armor token scanning cost and Apigee API management fees (v4 Pricing tab C)."
				/>
				<Row
					label="Model Armor $ per 1M tokens"
					step={0.01}
					value={params.modelArmorUsdPer1MTokens}
					onChange={(v) => setP({ modelArmorUsdPer1MTokens: v })}
				/>
				<Row
					label="Apigee base fee £/month"
					step={1}
					value={params.apigeeBaseFeeGbp}
					onChange={(v) => setP({ apigeeBaseFeeGbp: v })}
				/>
				<Row
					label="Apigee variable rate £ per 1M API calls"
					step={1}
					value={params.apigeeVariableRateGbpPer1MApiCalls}
					onChange={(v) => setP({ apigeeVariableRateGbpPer1MApiCalls: v })}
				/>

				<div className="note note-mt">
					Changes apply immediately to new estimates on the Calculator tab (browser session only — not persisted to the
					server).
				</div>
				<button type="button" className="btns" onClick={onReset}>
					Reset to server defaults
				</button>
			</div>
		</div>
	);
}
